import React, { cloneElement, isValidElement, ReactElement } from 'react';
import classNames from 'classnames';

import { ConditionalRenderComponent } from 'app/component/ConditionalRenderComponent';
import { FieldSetLayout } from 'app/component/fieldset/FieldSetLayout';

import ColorSchema from '../ColorSchema';

interface FieldSetProps {
    /**
     * Идентифицирующий css класс набора данных. (unique)
     */
    className: string;

    /**
     * Массив пропсов из которых строится компонент строки набора данных
     */
    fieldsProps: Array<FieldSetRowComponentProps>;

    /**
     * Предустановленные алиасы стилей для разных типов FieldSet.
     * По умолчанию {@link FieldSetLayout.DEFAULT}.
     */
    layout?: FieldSetLayout;

    /**
     * Цветовая схема.
     * Добавляется всем компонентам данных набора данных.
     * По умолчанию ColorSchema.DARK.
     */
    colorSchema?: ColorSchema;
}

interface FieldSetRowProps {
    /**
     * Идентифицирующий css класс строки набора данных. (unique in scope)
     */
    className: string;

    /**
     * Заголовок строки набора данных.
     */
    title: string;

    /**
     * true - рендерится элемент строки набора данных;
     * false - элемент строки набора данных не рендерится.
     *
     * По умолчанию - true.
     */
    isVisible?: boolean;

    /**
     * Цветовая схема.
     * Значение {@link FieldSetRowProps.colorSchema} может переопределять значение {@link FieldSetProps.colorSchema}
     */
    colorSchema?: ColorSchema;

    /**
     * Значение строки набора данных.
     */
    value: any;

    /**
     * Текст тултипа
     */
    tooltip?: Function;

    /**
     * Описание строки набора данных. Внедрена для отображения текста акции
     */
    description?: string;
}

interface FieldSetRowComponentProps extends FieldSetRowProps {
    /**
     * Предустановленные алиасы стилей.
     */
    layout?: FieldSetLayout;
}

type ColorSchemeComponentProps = { colorSchema: ColorSchema };

/**
 * Возвращает элемент:
 *  - элемент является реакт компонентом - вернет копию его и передаст в него цветовую схему;
 *  - элемент не реакт компонент - вернет его.
 * @param element - элемент.
 * @param colorSchema - цветовая схема.
 */
const cloneElementWithColorScheme = (
    element: any,
    colorSchema: ColorSchema,
) => {
    //hotfix для возможности передачи в компонент html разметки
    //todo: доработать isValidElement для отделения React компонентов от jsx тегов.
    if (isValidElement(element) && element.type != 'span') {
        const reactElement = element as ReactElement<ColorSchemeComponentProps>;
        return cloneElement(reactElement, {
            ...reactElement.props,
            colorSchema,
        });
    } else {
        return element;
    }
};

/**
 * Компонент отображает строку/элемент набора данных.
 * Ответственность:
 *  - унифицирует layout для типичных компонентов, построенных по принципу ключ - значение.
 *  - инкапсулирует логику видимости в зависимости от настроек.
 *
 * todo: Добавить after, before renderer?
 *
 */
const FieldSetRowComponent = (props: FieldSetRowComponentProps) => {
    const componentClassName = classNames(
        props.className,
        'fieldSetRow',
        props.layout,
    );

    return (
        <ConditionalRenderComponent
            condition={props.isVisible ?? true}
            alternative={null}
        >
            <div className={componentClassName}>
                <div className="fieldSetValues">
                    <div className={'titleValueWrapper'}>
                        <div className="title">{props.title}</div>
                        <div className="value">
                            {cloneElementWithColorScheme(
                                props.value,
                                props.colorSchema,
                            )}
                        </div>
                    </div>

                    {props.tooltip && (
                        <div className={'tooltip'}>{props.tooltip()}</div>
                    )}
                </div>
                {props.description && (
                    <div className="description">{props.description}</div>
                )}
            </div>
        </ConditionalRenderComponent>
    );
};

/**
 * Компонент для отображения набора данных.
 * Инкапсулирует базовый layout:
 *  - Родительский компонент с уникальным идентификатором;
 *  - Список компонентов {@link FieldSetRowComponent};
 *
 *  Настройки компонента (e.g wrapTitle) применяются ко всем дочерним компонентам {@link FieldSetRowComponent};
 *  Стилизация компонентов осуществляется по месту применения, в целях гибкости.
 */
export const FieldSet = ({
    className,
    fieldsProps,
    layout,
    colorSchema = ColorSchema.DARK,
}: FieldSetProps) => {
    const componentClassName = classNames(className, 'fieldSet', layout);

    return (
        <div className={componentClassName}>
            {fieldsProps.map(
                (fieldProps: FieldSetRowComponentProps, index: number) => (
                    <FieldSetRowComponent
                        layout={layout}
                        key={index}
                        colorSchema={fieldProps.colorSchema ?? colorSchema}
                        className={fieldProps.className}
                        title={fieldProps.title}
                        description={fieldProps.description}
                        isVisible={fieldProps.isVisible ?? true}
                        value={fieldProps.value}
                        tooltip={fieldProps.tooltip}
                    />
                ),
            )}
        </div>
    );
};
